/**
 * 일정 다듬기 — 지난 여행의 발자취에서 찾은 숫자.
 *
 * 숫자는 전부 서버 집계입니다. 화면은 계산하지 않고 그대로 보여 줍니다 —
 * 같은 계산을 두 벌 두면 한쪽만 고쳐집니다.
 *
 * 표본(sampleMeetingCount)이 0이면 섹션째 숨깁니다. 지난 여행이 없는
 * 사람에게 "찾은 게 없어요" 를 보여 줄 이유가 없습니다.
 */
import { useCallback, useEffect, useState } from 'react';
import { PlaceReviewRepository } from 'repository/placeReviewRepository';
import { unwrapData } from 'common/utils/apiResponse';

/** 예정 대비 실제 체류 한 줄 */
export interface InsightStay {
    placeName: string;
    dayNumber?: number | null;
    /** 다음 장소까지의 간격으로 본 계획 체류 (분) */
    plannedMin: number;
    /** 지난 여행들의 평균 실제 체류 (분) */
    actualMin: number;
    diffMin: number;
    diffPct: number;
    /** 서로 다른 여행 몇 번에서 봤는지 — 표본 크기 */
    tripCount: number;
    /** 초과가 문턱을 넘었는가 — 이것만 붉게 */
    over?: boolean | null;
}

/** 계획에 없이 들른 곳 */
export interface InsightOffSchedule {
    placeName: string;
    tripCount: number;
    avgStayMin: number;
}

export interface InsightSuggestion {
    /** STAY_ADJUST · PLACE_ADD · MOVE_MODE */
    type: string;
    title: string;
    /** 이유 없는 제안은 안 따릅니다 — 서버가 항상 채웁니다 */
    reason: string;
    payload?: Record<string, any> | null;
}

export interface TraceInsights {
    /** 표본 — 화면 머리에서 **먼저** 밝힙니다 */
    sampleMeetingCount: number;
    plannedPlaceCount: number;
    /** 계획 시각이 없어 견줄 수 없던 장소 수 — 숨기지 않습니다 */
    skippedNoPlannedTime: number;
    stays: InsightStay[];
    offSchedule: InsightOffSchedule[];
    moveMode?: { mode: string; code: string; legCount: number; tripCount: number } | null;
    suggestions: InsightSuggestion[];
}

export const useTraceInsights = (meetingId?: string | null, enabled = true) => {
    const [insights, setInsights] = useState<TraceInsights | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!meetingId || !enabled) return;
        setLoading(true);
        try {
            const res = await PlaceReviewRepository.getTraceInsights(meetingId);
            const data = unwrapData<TraceInsights>(res);
            /* 실패도 응답으로 옵니다(onRequest 가 에러를 삼킵니다).
               표본 수가 숫자가 아니면 받은 게 없다고 봅니다. */
            setInsights(data && typeof data.sampleMeetingCount === 'number' ? data : null);
        } catch {
            setInsights(null);
        } finally {
            setLoading(false);
        }
    }, [meetingId, enabled]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            await load();
        })();
        return () => { cancelled = true; };
    }, [load]);

    return { insights, loading, reload: load };
};
